import { useCallback } from 'react'
import { defineEntityMapPreference } from '@solum/experience'
import { MIN_COL_PX, MAX_COL_PX } from '@solum/ui'
import { useUserSettingPreference } from '../data/useUserSettingPreference'

/**
 * Per-user column widths, dragged on the grid header and remembered.
 *
 * ── Where they live, and why not in the view ────────────────────────────────
 * `core.user_setting` (key `grid.widths`), which needs NO migration and is
 * already the proven per-user round-trip — `my_settings()` to read,
 * `set_user_setting()` to write, the shape `data/userFormat.ts` uses for
 * locale/timezone/currency.
 *
 * NOT in `core.view`. A view is a SHARED definition — which columns, which
 * filters, which sort — and its personal forks are the D365 model Christopher
 * asked for. A column width is not that: it is a property of the screen you are
 * reading it on, not of what the list means. Two people sharing a view should
 * see the same rows and the same columns; they should not have to agree on how
 * wide their monitors are. Putting width in the view would also make every drag
 * dirty the draft and prompt "save as my view", which turns a glance into a
 * decision.
 *
 * ── Shape ───────────────────────────────────────────────────────────────────
 *   { "<entity>": { "<column>": <px> } }
 * Keyed by ENTITY, not by view: the same column in two views of the same entity
 * is the same column, and having it jump width when you switch tab would read as
 * a bug. Unknown/removed columns are simply never looked up — no cleanup needed.
 *
 * ── Writes are DEBOUNCED, deliberately ──────────────────────────────────────
 * A drag fires a mousemove per pixel. Writing on each would be hundreds of RPCs
 * for one gesture; the local state updates every frame and the server sees the
 * result once the hand stops.
 */
const KEY = 'grid.widths'
const SAVE_DEBOUNCE_MS = 600

/* The bounds live with the GRID (#959) — they are what the resize control
   announces as `aria-valuemin`/`aria-valuemax`, and a stored width clamped
   against a different pair from the one announced is the two drifting. Re-
   exported so this module's existing importers are unchanged. */
export { MIN_COL_PX, MAX_COL_PX } from '@solum/ui'

const preference = defineEntityMapPreference<number>({
  settingKey: KEY,
  // Reading validates the stored fact but does not reinterpret it. Bounds and
  // integer rounding apply only to a person's new resize gesture.
  decode: (value) => typeof value === 'number' && Number.isFinite(value) ? value : null,
})

export interface ColumnWidths {
  /** the override in px, or null when this column has never been resized */
  widthOf: (column: string) => number | null
  /** live-set during a drag; persisted once the gesture settles */
  set: (column: string, px: number) => void
  /** back to the surface's declared width */
  reset: (column: string) => void
  /** true once the stored widths have been read — until then, do not persist */
  ready: boolean
}

export function useColumnWidths(entity: string): ColumnWidths {
  const state = useUserSettingPreference(
    preference, { entity }, entity, SAVE_DEBOUNCE_MS,
  )

  const set = useCallback((column: string, px: number) => {
    const clamped = Math.max(MIN_COL_PX, Math.min(MAX_COL_PX, Math.round(px)))
    state.update({ slot: column, value: clamped })
  }, [state.update])

  const reset = useCallback((column: string) => {
    state.update({ slot: column, value: null })
  }, [state.update])

  return {
    widthOf: (column) => state.value[column] ?? null,
    set,
    reset,
    ready: state.ready,
  }
}
